import { ElementType, FC, ForwardedRef, forwardRef, ReactNode } from 'react';
import Tooltip, { TooltipData } from '../tooltip/tooltip';

export type ButtonVariant = 'normal' | 'ghost' | 'borderless';
export type ButtonType = 'submit' | 'reset' | 'button';
export type ButtonColor = 'primary' | 'neutral' | 'error';

export interface ButtonProps {
  title?: ReactNode;
  icon?: ReactNode;
  className?: string;
  onClickHandler?: any;
  disabled?: boolean;
  infoData?: TooltipData;
  type?: ButtonType;
  variant?: ButtonVariant;
  color?: ButtonColor;
  isMobileAddBtn?: boolean;
  isLoading?: boolean;
  testId?: string;
  Tag?: ElementType | FC;
  tagProps?: any;
}

const classes = 'group relative flex w-max items-center justify-center gap-3 overflow-hidden rounded-full px-6 py-2 text-base font-medium transition-all [&_path]:transition-all';

const Button = forwardRef(
  (
    { title, icon, className, onClickHandler, disabled = false, infoData, type = 'button', variant = 'normal', color = 'primary',
      isMobileAddBtn = false, isLoading, testId, Tag = 'button', tagProps = {} }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const colors = {
      primary: {
        normal: 'bg-primary text-white [&_path]:stroke-white',
        ghost: 'bg-transparent hover:bg-primary hover:text-white text-primary border border-primary [&_path]:stroke-primary [&_path]:hover:stroke-white',
        borderless: 'bg-transparent text-primary [&_path]:stroke-primary',
      },
      neutral: {
        normal: 'bg-gray-2 text-gray-1 [&_path]:stroke-gray-1',
        ghost: 'bg-transparent hover:bg-gray-2 text-gray-1 border border-gray-2 [&_path]:stroke-gray-1',
        borderless: 'bg-transparent text-gray-1 [&_path]:stroke-gray-1',
      },
      error: {
        normal: 'bg-error text-white [&_path]:stroke-white',
        ghost: 'bg-transparent hover:bg-error hover:text-white text-error border border-error [&_path]:stroke-error [&_path]:hover:stroke-white',
        borderless: 'bg-transparent text-error [&_path]:stroke-error',
      },
    };

    const loadingAnimation = {
      primary: {
        normal: 'from-primary via-[#61acff] to-primary',
        ghost: 'from-transparent via-[#d2e7ff] to-transparent',
        borderless: 'from-transparent via-[#d2e7ff] to-transparent',
      },
      neutral: {
        normal: 'from-gray-2 via-gray-300 to-gray-2',
        ghost: 'from-transparent via-gray-2 to-transparent',
        borderless: 'from-transparent via-gray-2 to-transparent',
      },
      error: {
        normal: 'from-error via-[#ff8078] to-error',
        ghost: 'from-transparent via-[#ffe2e0] to-transparent',
        borderless: 'from-transparent via-[#ffe2e0] to-transparent',
      },
    };

    return (
      <>
        <Tag
          {...tagProps}
          data-testid={testId}
          ref={ref}
          className={`${classes} ${disabled ? 'pointer-events-none' : ''} ${className} ${disabled ? colors['neutral']?.[variant] : colors[color]?.[variant]} ${isMobileAddBtn ? 'fixed bottom-4 right-4 z-50 h-[64px] w-[64px] md:static md:z-0 md:h-auto md:w-auto' : ''}`}
          onClick={() => onClickHandler?.()}
          type={type}
          disabled={disabled}>
          {icon && <span className='z-10'>{icon}</span>}

          {title && <span className={`z-10 ${isMobileAddBtn ? 'sr-only md:not-sr-only' : ''}`}>{title}</span>}

          {isLoading && (
            <span
              className={`absolute inset-0 animate-sweep bg-gradient-to-r bg-[length:200%] ${disabled ? loadingAnimation['neutral']?.[variant] : loadingAnimation[color]?.[variant]}`}></span>
          )}
        </Tag>

        {infoData && (
          <Tooltip
            infoData={infoData}
            className='ml-2'
          />
        )}
      </>
    );
  }
);

Button.displayName = 'Button';

export default Button;
